import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Wine, Leaf, Snowflake, Beaker, ChefHat, X, ExternalLink, Image as ImageIcon, Layers, Search, FolderTree, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import QuestionsSection from "@/components/QuestionsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "decks", label: "Decks", icon: Layers },
  { id: "categories", label: "Categories", icon: FolderTree },
  { id: "questions", label: "Questions", icon: HelpCircle },
  { id: "glassware", label: "Glassware", icon: Wine },
  { id: "garnish", label: "Garnish", icon: Leaf },
  { id: "ice", label: "Ice", icon: Snowflake },
  { id: "method", label: "Method", icon: ChefHat },
  { id: "ingredient", label: "Ingredients", icon: Beaker },
];

/* ─── Decks Management Section ─── */
function DecksSection() {
  const [decks, setDecks] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editDeck, setEditDeck] = useState(null);
  const [deckName, setDeckName] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [dRes, cRes] = await Promise.all([axios.get(`${API}/decks`), axios.get(`${API}/cocktails`)]);
    setDecks(dRes.data);
    setCocktails(cRes.data);
  };
  useEffect(() => { load(); }, []);

  const cocktailMap = Object.fromEntries(cocktails.map(c => [c.id, c]));
  const filtered = cocktails.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));

  const openNew = () => { setEditDeck(null); setDeckName(""); setSelectedIds([]); setSearch(""); setShowDialog(true); };
  const openEdit = (d) => { setEditDeck(d); setDeckName(d.name); setSelectedIds(d.cocktail_ids || []); setSearch(""); setShowDialog(true); };

  const toggleCocktail = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const save = async () => {
    if (!deckName.trim()) { toast.error("Name is required"); return; }
    try {
      if (editDeck) {
        await axios.put(`${API}/decks/${editDeck.id}`, { name: deckName, cocktail_ids: selectedIds });
        toast.success("Deck updated");
      } else {
        await axios.post(`${API}/decks`, { name: deckName, cocktail_ids: selectedIds });
        toast.success("Deck created");
      }
      setShowDialog(false);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deck?")) return;
    await axios.delete(`${API}/decks/${id}`);
    toast.success("Deck deleted");
    load();
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="add-deck-btn"><Plus size={16} className="mr-1" /> New Deck</Button>
      </div>

      {decks.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Layers size={40} className="mx-auto text-[hsl(var(--muted-foreground))]" />
          <p className="text-[hsl(var(--muted-foreground))]">No decks yet. Create your first deck to group cocktails.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {decks.map(deck => (
            <div key={deck.id} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`deck-${deck.id}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-[hsl(var(--secondary))]">
                <h3 className="font-bold text-sm uppercase tracking-wide">{deck.name}</h3>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(deck)} data-testid={`edit-deck-${deck.id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(deck.id)} data-testid={`delete-deck-${deck.id}`}><Trash2 size={14} className="text-red-400" /></Button>
                </div>
              </div>
              {(deck.cocktail_ids || []).length === 0 ? (
                <p className="px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">No cocktails in this deck</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1 p-2">
                  {deck.cocktail_ids.map(cid => {
                    const c = cocktailMap[cid];
                    if (!c) return null;
                    return (
                      <div key={cid} className="text-center">
                        <div className="aspect-square rounded overflow-hidden bg-[hsl(var(--secondary))]">
                          {c.image_url ? <img src={`${BACKEND}${c.image_url}`} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Wine size={16} className="text-[hsl(var(--muted-foreground))]" /></div>}
                        </div>
                        <p className="text-[9px] mt-1 text-[hsl(var(--muted-foreground))] truncate leading-tight">{c.name}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Deck Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDeck ? "Edit Deck" : "New Deck"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Deck Name *</Label>
              <Input value={deckName} onChange={e => setDeckName(e.target.value)} placeholder="e.g. Gin Cocktails" className="bg-[hsl(var(--secondary))]" data-testid="deck-name-input" />
            </div>

            <div>
              <Label>Cocktails ({selectedIds.length} selected)</Label>
              <div className="relative mt-1 mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cocktails..." className="pl-9 bg-[hsl(var(--secondary))]" data-testid="deck-cocktail-search" />
              </div>
              <div className="grid grid-cols-3 gap-1.5 max-h-[40vh] overflow-y-auto p-1">
                {filtered.map(c => {
                  const selected = selectedIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleCocktail(c.id)}
                      className={`rounded-lg overflow-hidden border-2 transition-all ${selected ? 'border-[hsl(var(--primary))] ring-1 ring-[hsl(var(--primary))]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      data-testid={`deck-cocktail-${c.id}`}
                    >
                      <div className="aspect-square bg-[hsl(var(--secondary))]">
                        {c.image_url ? <img src={`${BACKEND}${c.image_url}`} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Wine size={20} className="text-[hsl(var(--muted-foreground))]" /></div>}
                      </div>
                      <p className="text-[10px] font-medium text-center py-1 px-0.5 truncate">{c.name}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={save} className="flex-1" data-testid="deck-save-btn">{editDeck ? "Save Changes" : "Create Deck"}</Button>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Categories Section — group cocktails by their `category` field ─── */
function CategoriesSection() {
  const [cocktails, setCocktails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameFrom, setRenameFrom] = useState("");
  const [renameTo, setRenameTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cocktails`);
      setCocktails(res.data || []);
    } catch { setCocktails([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Group by category — preserve insertion order, fall back to "Uncategorized"
  const grouped = cocktails.reduce((acc, c) => {
    const key = (c.category || "Uncategorized").trim() || "Uncategorized";
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});
  const categories = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));

  const startRename = (from) => { setRenameFrom(from); setRenameTo(from); setRenameOpen(true); };
  const saveRename = async () => {
    const target = renameTo.trim();
    if (!target || target === renameFrom) { setRenameOpen(false); return; }
    try {
      // Re-tag every cocktail in this category to the new label
      const affected = grouped[renameFrom] || [];
      await Promise.all(affected.map(c =>
        axios.put(`${API}/cocktails/${c.id}`, { ...c, category: target })
      ));
      toast.success(`Renamed "${renameFrom}" → "${target}" (${affected.length} cocktails)`);
      setRenameOpen(false);
      load();
    } catch {
      toast.error("Failed to rename category");
    }
  };

  if (loading) {
    return <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-8">Loading...</p>;
  }
  if (categories.length === 0) {
    return (
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <CardContent className="py-10 text-center space-y-2">
          <FolderTree size={28} className="mx-auto text-[hsl(var(--muted-foreground))]" />
          <p className="text-sm font-medium">No categories yet</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Categories are set on individual cocktails. Upload a CSV with a <code>Category</code> column or edit a cocktail to assign one.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
        Categories group your cocktails (e.g. "Signature", "Classic", "St-Germain Terrace Menu"). Edit any cocktail to move it between categories — or rename a whole category in one click below.
      </p>
      <div className="space-y-2" data-testid="categories-list">
        {categories.map(([cat, items]) => {
          const open = !!expanded[cat];
          return (
            <Card key={cat} className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden" data-testid={`category-${cat}`}>
              <button
                type="button"
                onClick={() => setExpanded(prev => ({ ...prev, [cat]: !prev[cat] }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--secondary))]/40 transition-colors"
                data-testid={`category-toggle-${cat}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FolderTree size={16} className="text-[hsl(var(--primary))] shrink-0" />
                  <span className="font-semibold truncate">{cat}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] shrink-0">{items.length}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); startRename(cat); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); startRename(cat); } }}
                    className="text-[11px] underline text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] cursor-pointer"
                    data-testid={`rename-category-${cat}`}
                  >
                    Rename
                  </span>
                  {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </button>
              {open && (
                <div className="border-t border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]">
                  {items.map(c => (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-2 hover:bg-[hsl(var(--secondary))]/30" data-testid={`category-item-${c.id}`}>
                      <Wine size={14} className="text-[hsl(var(--muted-foreground))]" />
                      <span className="flex-1 text-sm truncate">{c.name}</span>
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{c.base_spirit}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="rename-category-dialog">
          <DialogHeader>
            <DialogTitle>Rename category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Every cocktail currently in <span className="font-semibold text-[hsl(var(--foreground))]">{renameFrom}</span> will be moved to the new category.
            </p>
            <Input
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
              placeholder="New category name"
              className="bg-[hsl(var(--secondary))]"
              data-testid="rename-category-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button>
              <Button onClick={saveRename} data-testid="rename-category-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Library Items Section ─── */
function LibraryItemsSection({ activeTab }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchItems = async (type) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/library?type=${type}`);
      setItems(res.data);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { fetchItems(activeTab); }, [activeTab]);

  const openNew = () => { setEditItem({ name: "", description: "", youtube_link: "", image_url: null, type: activeTab }); setShowDialog(true); };
  const openEdit = (item) => { setEditItem({ ...item }); setShowDialog(true); };

  const handleSave = async () => {
    if (!editItem.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editItem.id) {
        await axios.put(`${API}/library/${editItem.id}`, editItem);
      } else {
        await axios.post(`${API}/library`, editItem);
      }
      toast.success(editItem.id ? "Updated" : "Created");
      setShowDialog(false);
      fetchItems(activeTab);
    } catch { toast.error("Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this library item?")) return;
    try {
      await axios.delete(`${API}/library/${id}`);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success("Deleted");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to delete";
      toast.error(msg);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editItem?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/library/${editItem.id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditItem(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success("Image uploaded");
    } catch { toast.error("Failed to upload"); }
    setUploading(false);
  };

  const handleImageRemove = async () => {
    if (!editItem?.id || !editItem?.image_url) return;
    if (!window.confirm("Remove the image from this library item?")) return;
    setUploading(true);
    try {
      await axios.delete(`${API}/library/${editItem.id}/image`);
      setEditItem(prev => ({ ...prev, image_url: null }));
      setItems(prev => prev.map(i => i.id === editItem.id ? { ...i, image_url: null } : i));
      toast.success("Image removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove image");
    }
    setUploading(false);
  };

  const TabIcon = TABS.find(t => t.id === activeTab)?.icon || Wine;

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="add-library-item-btn"><Plus size={16} className="mr-1" /> Add {TABS.find(t => t.id === activeTab)?.label}</Button>
      </div>

      {loading ? (
        <p className="text-center text-[hsl(var(--muted-foreground))] py-8">Loading...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <TabIcon size={40} className="mx-auto text-[hsl(var(--muted-foreground))]" />
          <p className="text-[hsl(var(--muted-foreground))]">No {activeTab} items yet</p>
          <Button variant="outline" onClick={openNew}><Plus size={14} className="mr-1" /> Add First Item</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="library-grid">
          {items.map(item => (
            <Card key={item.id} className="bg-[hsl(var(--card))] border-[hsl(var(--border))] overflow-hidden group cursor-pointer hover:border-[hsl(var(--primary))]/40 transition-colors" onClick={() => openEdit(item)} data-testid={`library-item-${item.id}`}>
              <div className="aspect-square bg-[hsl(var(--secondary))] relative">
                {item.image_url ? <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={32} className="text-[hsl(var(--muted-foreground))]/30" /></div>}
                <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="absolute top-2 right-2 p-2 rounded-full bg-black/70 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-red-500 active:bg-red-500 z-10" aria-label="Delete item" data-testid={`delete-lib-${item.id}`}><Trash2 size={14} /></button>
                {item.youtube_link && <div className="absolute bottom-2 right-2 p-1 rounded bg-red-600 text-white"><ExternalLink size={10} /></div>}
              </div>
              <CardContent className="p-3">
                <p className="font-medium text-sm truncate">{item.name}</p>
                {item.description && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mt-1">{item.description}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem?.id ? "Edit" : "New"} {TABS.find(t => t.id === activeTab)?.label} Item</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="flex items-center gap-4">
                  {editItem.image_url ? (
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-[hsl(var(--secondary))] shrink-0"><img src={`${BACKEND}${editItem.image_url}`} alt="" className="w-full h-full object-cover" /></div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0"><ImageIcon size={20} className="text-[hsl(var(--muted-foreground))]" /></div>
                  )}
                  {editItem.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="cursor-pointer"><input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" /><Button type="button" variant="outline" size="sm" disabled={uploading} asChild><span><Upload size={14} className="mr-1" />{uploading ? "Working..." : (editItem.image_url ? "Replace" : "Upload")}</span></Button></label>
                      {editItem.image_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleImageRemove}
                          disabled={uploading}
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid="lib-remove-image-btn"
                        >
                          <Trash2 size={14} className="mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">Save first, then upload image</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={editItem.name} onChange={e => setEditItem(prev => ({ ...prev, name: e.target.value }))} className="bg-[hsl(var(--secondary))]" data-testid="lib-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editItem.description || ""} onChange={e => setEditItem(prev => ({ ...prev, description: e.target.value }))} className="bg-[hsl(var(--secondary))] min-h-[80px]" data-testid="lib-description-input" />
              </div>
              {activeTab !== 'ice' && (
                <div className="space-y-2">
                  <Label>YouTube Link</Label>
                  <Input value={editItem.youtube_link || ""} onChange={e => setEditItem(prev => ({ ...prev, youtube_link: e.target.value }))} placeholder="https://youtube.com/watch?v=..." className="bg-[hsl(var(--secondary))]" data-testid="lib-youtube-input" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} className="flex-1" data-testid="lib-save-btn">{editItem.id ? "Save Changes" : "Create"}</Button>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Main Library Page ─── */
export default function LibraryPage() {
  const [activeTab, setActiveTab] = useState("glassware");

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-serif">FlashSpec Library</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Manage decks, images and info for FlashSpecs</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="library-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-[hsl(var(--primary))] text-black" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === "decks" ? (
        <DecksSection />
      ) : activeTab === "categories" ? (
        <CategoriesSection />
      ) : activeTab === "questions" ? (
        <QuestionsSection />
      ) : (
        <LibraryItemsSection activeTab={activeTab} />
      )}
    </div>
  );
}
